/** One supporting-file source on the unified claim as `additional_sources`. */
export type AdditionalSourceItem = {
  source_id: string;
  file_extension: string;
  file_name: string;
};

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts[parts.length - 1] ?? '';
}

function readSourceId(row: unknown): string | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;
  const sourceId = (row as Record<string, unknown>).source_id;
  return typeof sourceId === 'string' && sourceId ? sourceId : null;
}

/**
 * Maps generate response rows 1:1 with uploaded files (same order as the request),
 * using the `{ file_name, file_extension, source_id }` shape expected by the claim payload.
 */
export function buildAdditionalSourcesFromGenerateResponse(
  response: unknown,
  files: readonly File[]
): AdditionalSourceItem[] {
  const urlList = Array.isArray(response) ? response : response != null ? [response] : [];
  return files.flatMap((file, index) => {
    const sourceId = readSourceId(urlList[index]);
    if (!sourceId) return [];
    return [
      {
        source_id: sourceId,
        file_extension: getFileExtension(file.name),
        file_name: file.name,
      },
    ];
  });
}

/** Removes sources that belong to the given file (by file name). */
export function removeAdditionalSourcesForFile(
  sources: readonly AdditionalSourceItem[],
  file: File
): AdditionalSourceItem[] {
  return sources.filter((source) => source.file_name !== file.name);
}

/** Builds a single sources entry from a parsed document row when multi-file tracking is unavailable. */
export function fileSourceFromParsedDocument(
  doc: Record<string, unknown> | null | undefined
): AdditionalSourceItem | null {
  if (!doc) return null;
  const sourceId =
    (typeof doc.source_id === 'string' && doc.source_id.trim()) ||
    (typeof doc.sourceId === 'string' && doc.sourceId.trim()) ||
    '';
  if (!sourceId) return null;
  const fileName =
    (typeof doc.file_name === 'string' && doc.file_name.trim()) ||
    (typeof doc.filename === 'string' && doc.filename.trim()) ||
    '';
  if (!fileName) return null;
  const parts = fileName.split('.');
  return {
    source_id: sourceId,
    file_extension: parts.length > 1 ? (parts[parts.length - 1] ?? '') : '',
    file_name: fileName,
  };
}

/** Prefer tracked upload sources; fall back to a single parsed-document source. */
export function resolveDocumentFileSources(
  tracked: readonly AdditionalSourceItem[] | null | undefined,
  parsedClaimDocument: Record<string, unknown> | null | undefined
): AdditionalSourceItem[] {
  if (tracked && tracked.length > 0) return [...tracked];
  const fromParsed = fileSourceFromParsedDocument(parsedClaimDocument);
  return fromParsed ? [fromParsed] : [];
}
